/**
 * Microsoft Clarity adapter.
 * SDK 로드와 이벤트 송출을 모두 이 어댑터가 담당한다.
 *
 * FP-1152: 세션 리플레이/커스텀 이벤트는 analytics 카테고리.
 *
 * FP-1164: **SDK 로드를 앱 블록에서 이 어댑터로 가져왔다.**
 *   이전에는 Shopify 앱 블록(`shopify://apps/microsoft-clarity/blocks/clarity_js`)이 SDK 를
 *   로드하고, 앱이 Shopify 동의를 직접 읽어 게이팅한다고 보고 어댑터는 래핑만 했다.
 *   2026-08-11 실측에서 그 전제가 깨졌다 — 거부 확정 상태에서 `CLID` 쿠키가 생기고
 *   `b.clarity.ms/collect` 로 전송된다. ("쿠키 없는 제한 모드" 는 `_clck`·`_clsk` 에만 해당)
 *   앱 블록은 `config/settings_data.json` 에서 disabled 로 껐고, 여기서 analytics 동의 뒤에만
 *   로드한다. 동의 전에는 `clarity.ms` 요청조차 나가지 않는다.
 */
(function () {
  'use strict';
  if (!window.GBTrack) { console.warn('[GBTrack:clarity] core not loaded'); return; }

  var enabled = true;
  var projectId = null;
  var sdkRequested = false;
  var readyCbs = [];

  function analyticsAllowed() {
    var c = window.GBConsent;
    return !!(c && c.isAllowed('analytics'));
  }

  // Clarity 표준 로더. analytics 동의가 확인된 뒤 1회만 호출된다.
  function loadSdk() {
    if (sdkRequested || !projectId) return;
    sdkRequested = true;
    // 로드 직전 재확인 — whenAllowed 등록 후 철회됐을 수 있다.
    if (!analyticsAllowed()) { sdkRequested = false; return; }
    try {
      (function (c, l, a, r, i) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        var t = l.createElement(r);
        t.async = 1;
        t.src = 'https://www.clarity.ms/tag/' + i;
        var y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, 'clarity', 'script', projectId);
      // 표준 로더는 `window.clarity` 큐 스텁을 동기적으로 만든다. 태그 다운로드 완료 시
      // 큐가 replay 되므로 이 시점부터 send() 를 허용해도 이벤트가 유실되지 않는다.
      markReady();
    } catch (e) {
      console.warn('[GBTrack:clarity] SDK 로드 실패', e);
      enabled = false;
    }
  }

  function markReady() {
    var cbs = readyCbs;
    readyCbs = [];
    cbs.forEach(function (cb) {
      try { cb(); } catch (e) { console.warn('[GBTrack:clarity] onReady 콜백 실패', e); }
    });
  }

  // 철회 시 Clarity 가 심은 쿠키·스토리지를 훑어 지운다.
  function purgeClarityStorage() {
    try {
      ['_clck', '_clsk', 'CLID'].forEach(function (name) {
        document.cookie = name + '=; Max-Age=0; path=/';
        document.cookie = name + '=; Max-Age=0; path=/; domain=' + location.hostname;
      });
    } catch (e) {
      // 쿠키 접근 차단 → 삭제 실패. 식별자가 남을 수 있으므로 기록은 남긴다.
      window.GBTrack.logError('clarity:purgeCookies', e);
    }
    [localStorage, sessionStorage].forEach(function (store) {
      try {
        Object.keys(store).forEach(function (k) {
          if (/^_clck|^_clsk|^_cltk|^clarity/i.test(k)) store.removeItem(k);
        });
      } catch (e) {
        // 스토리지 차단 → 삭제 실패. 식별자가 남을 수 있으므로 기록은 남긴다.
        window.GBTrack.logError('clarity:purgeStorage', e);
      }
    });
  }

  var adapter = {
    name: 'clarity',
    init: function (adapterCfg) {
      if (!adapterCfg || !adapterCfg.id || !String(adapterCfg.id).trim()) {
        console.warn('[GBTrack:clarity] project id 비어 있음 → 어댑터 비활성');
        enabled = false; return;
      }
      projectId = String(adapterCfg.id).trim();

      var consent = window.GBConsent;
      if (!consent || typeof consent.whenAllowed !== 'function') {
        console.warn('[GBTrack:clarity] GBConsent 미로드 → SDK 로드 보류(fail-closed)');
        enabled = false; return;
      }
      consent.whenAllowed('analytics', loadSdk);
    },
    isReady: function () {
      return enabled && analyticsAllowed() && typeof window.clarity === 'function';
    },
    // SDK 로드가 동의 뒤로 미뤄졌으므로 폴링하면 "동의 대기"를 로드 실패로 오판하고,
    // 거부한 이용자에게는 세션 내내 도는 인터벌이 남는다. 로더가 직접 통지하는 방식을 쓴다.
    onReady: function (cb) {
      if (!enabled) return;
      if (adapter.isReady()) { cb(); return; }
      readyCbs.push(cb);
    },
    transform: function (event, props, context) {
      // Clarity: event 이름만 + 구분용 tag 몇 개
      var tags = [];
      if (context.template) tags.push('template:' + context.template);
      if (context.customer_logged_in) tags.push('logged_in');
      // A/B 테스트 참여 그룹 — 그룹별 녹화·히트맵 필터 (docs/AB-TEST.md)
      if (context.abt) tags.push('abt:' + String(context.abt).slice(0, 64));
      // Clarity tags는 세션 필터링 용도 (고카디널리티 금지).
      // number 는 일반적으로 id/price 같은 고카디널리티라 제외, string만 tag화.
      Object.keys(props).forEach(function (k) {
        var v = props[k];
        if (typeof v === 'string') {
          tags.push(k + ':' + v.slice(0, 64));
        }
      });
      return { event: event, tags: tags };
    },
    isAllowed: function () { return enabled && analyticsAllowed(); },
    send: function (payload) {
      if (!enabled || !analyticsAllowed() || typeof window.clarity !== 'function') return;
      window.clarity('event', payload.event);
      (payload.tags || []).forEach(function (t) {
        var i = t.indexOf(':');
        if (i > 0) window.clarity('set', t.slice(0, i), t.slice(i + 1));
      });
    },

    // FP-1164 · 철회: 녹화 중단 + Clarity 가 심은 쿠키·스토리지 삭제.
    onConsentRevoked: function (revoked) {
      if (!revoked.analytics) return;
      if (typeof window.clarity === 'function') {
        try { window.clarity('stop'); } catch (e) { console.warn('[GBTrack:clarity] stop 실패', e); }
      }
      purgeClarityStorage();
    },

    // 재동의: SDK 를 한 번도 안 받았다면 이제 받고, 이미 받았다면 녹화를 다시 시작한다.
    onConsentRestored: function (restored) {
      if (!restored.analytics) return;
      if (!sdkRequested) { loadSdk(); return; }
      if (typeof window.clarity === 'function') {
        try { window.clarity('start'); } catch (e) { console.warn('[GBTrack:clarity] start 실패', e); }
      }
    },
  };

  window.GBTrack.register(adapter);
})();
